import { put, takeLatest, select, take, all } from 'redux-saga/effects'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { seekVideoToTimestamp } from '@tabeeb/modules/player/actions'
import { getIsCurrentUserReviewer } from '@tabeeb/modules/../users/selectors'
import {
  deleteGalleryItemSuccess,
  onDeleteGalleryItem,
  onGalleryItemSwitched,
  onSelectGalleryItem,
  onSelectReadonlyGalleryItem,
  selectGalleryItem,
} from '@tabeeb/modules/gallery/actions'
import { onContentReviewCompleted, onReviewFeedbackSent } from '@tabeeb/modules/contentReviews/actions'
import { getReadonlyContentFormSuccess, getReadonlyFormAnswerByTabSuccess } from '@tabeeb/shared/content/actions'
import { getIsContentReadonly } from '@tabeeb/modules/readonlyWhiteboard/selectors'
import { SagaIterator } from 'redux-saga'
import { Action } from 'redux-actions'
import { ContentFormListResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { signalrEvents, signalrConstants } from '../../signalr'
import { formTabsIdsEquals } from '../services/formTabService'
import { getCurrentUserReview } from '../../contentReviews/selectors'
import {
  getAnswerStatus,
  getCurrentForm,
  getDisplayAnswersMode,
  getSelectedGalleryItemId,
  getSelectedUserId,
} from '../selectors'
import {
  clearFormAnswers,
  getAllMyLatestAnswers,
  getCounterInfo,
  getFormAnswers,
  getLatestFormAnswers,
  apiGetLatestFormAnswers,
  getLatestTabsAnswers,
  getTabsAnswers,
  proceedToControlAnswerPage,
  setAnswersStatusesCount,
  setDisplayAnswersMode,
  setFormAnswers,
  setFormAnswersLoading,
  setSelectedRecord,
  setSelectedTabs,
  setSelectedUserId,
  updateAnswerStatus,
  updateFormAnswers,
  getFormAnswersByRecord,
} from '../actions'
import { DisplayAnswersMode } from '../enums'
import { FormControlAnswer } from '../types'

function* onGetAllMyLatestAnswers(): SagaIterator {
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  const selectedTabs = currentForm.selectedTabs || []
  yield put(setSelectedUserId(currentUserId))

  if (selectedTabs?.length > 0) {
    yield put(
      getTabsAnswers.request({
        currentFormId: currentForm.Id!,
        selectedUserId: currentUserId,
        formTabs: selectedTabs.map((tabId) => `&formTabs=${tabId}`).join(''),
        pageId: null,
      })
    )
  } else {
    yield put(
      getFormAnswers.request({
        contentFormId: currentForm.Id!,
        userId: currentUserId,
        pageId: null,
      })
    )
  }
}

function* onSetFormAnswers(action: ReturnType<typeof getFormAnswers.success>): SagaIterator {
  const answers = action.response.data
  const isReviewer: ReturnType<typeof getIsCurrentUserReviewer> = yield select(getIsCurrentUserReviewer)
  const review: ReturnType<typeof getCurrentUserReview> = yield select(getCurrentUserReview)

  yield put(
    setFormAnswers(
      answers.map((answer) => ({
        ...answer,
        FormAnswerStatus: getAnswerStatus(answer, isReviewer, review),
      }))
    )
  )
}

function* onSetReadonlyFormAnswers({ payload }: ReturnType<typeof getReadonlyContentFormSuccess>): SagaIterator {
  yield put(setFormAnswers(payload.LatestFormAnswers))
}

function* onProceedToControlAnswerPage({
  payload: answer,
}: ReturnType<typeof proceedToControlAnswerPage>): SagaIterator {
  const selectedGalleryItemId: ReturnType<typeof getSelectedGalleryItemId> = yield select(getSelectedGalleryItemId)
  if (selectedGalleryItemId !== answer.PageId) {
    const isContentReadonly: ReturnType<typeof getIsContentReadonly> = yield select(getIsContentReadonly)
    if (isContentReadonly) {
      yield put(onSelectReadonlyGalleryItem({ pageId: answer.PageId, timestamp: answer.Timestamp }))
    } else {
      yield put(onSelectGalleryItem({ pageId: answer.PageId, timestamp: answer.Timestamp, updateWeb: true }))
    }
    yield take([onGalleryItemSwitched])
  } else if (answer.Timestamp && answer.Timestamp >= 0) {
    yield put(seekVideoToTimestamp(answer.Timestamp * 1000))
  }
}

function* onGetLatestFormAnswers(): SagaIterator {
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const tabsPanels = currentForm.Tabspanels || []
  const selectedTabs = currentForm.selectedTabs || tabsPanels.map((panel) => panel.Tabs![0].Id)

  let formTabs

  if (Array.isArray(selectedTabs)) {
    formTabs = selectedTabs.map((tabId) => `&formTabs=${tabId}`).join('')
  }

  const isReviewer: ReturnType<typeof getIsCurrentUserReviewer> = yield select(getIsCurrentUserReviewer)
  const isFormHasTabs = tabsPanels.length > 0
  let contentSharingId = null

  if (isReviewer) {
    const review: ReturnType<typeof getCurrentUserReview> = yield select(getCurrentUserReview)
    if (!review) {
      return
    }

    contentSharingId = review.Id
  }

  if (isFormHasTabs) {
    yield put(
      getLatestTabsAnswers.request({
        contentFormId: currentForm.Id!,
        formTabs: formTabs!,
        contentSharingId,
      })
    )
  } else {
    yield put(
      apiGetLatestFormAnswers.request({
        contentFormId: currentForm.Id!,
        contentSharingId,
      })
    )
  }
}

function* onGetFormAnswers(): SagaIterator {
  const selectedUserId: ReturnType<typeof getSelectedUserId> = yield select(getSelectedUserId)
  const displayAnswersMode: ReturnType<typeof getDisplayAnswersMode> = yield select(getDisplayAnswersMode)
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const selectedGalleryItemId: ReturnType<typeof getSelectedGalleryItemId> = yield select(getSelectedGalleryItemId)

  if (!currentForm?.ContentFormId) {
    return
  }

  yield put(setFormAnswersLoading(true))
  yield put(getCounterInfo())

  const selectedTabs = currentForm.selectedTabs || []
  const selectedRecord = currentForm.selectedRecord || 0

  if (selectedRecord > 0) {
    yield put(
      getFormAnswersByRecord.request({
        recordId: selectedRecord,
        formTabs: selectedTabs.map((tabId) => `&formTabs=${tabId}`).join(''),
      })
    )
  } else {
    switch (displayAnswersMode) {
      case DisplayAnswersMode.AllLatest:
        yield put(getLatestFormAnswers())
        break
      case DisplayAnswersMode.AllMyLatest:
        yield put(getAllMyLatestAnswers())
        break
      default:
        if (selectedTabs?.length > 0) {
          yield put(
            getTabsAnswers.request({
              currentFormId: currentForm.Id!,
              selectedUserId,
              formTabs: selectedTabs.map((tabId) => `&formTabs=${tabId}`).join(''),
              pageId: selectedGalleryItemId,
            })
          )
        } else {
          yield put(
            getFormAnswers.request({
              contentFormId: currentForm.Id!,
              userId: selectedUserId,
              pageId: selectedGalleryItemId,
            })
          )
        }
    }
  }
}

function* onAnswersCreated(action: Action<[FormControlAnswer]>): SagaIterator {
  const isReviewer: ReturnType<typeof getIsCurrentUserReviewer> = yield select(getIsCurrentUserReviewer)
  const selectedUserId: ReturnType<typeof getSelectedUserId> = yield select(getSelectedUserId)
  const displayAnswersMode: ReturnType<typeof getDisplayAnswersMode> = yield select(getDisplayAnswersMode)
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const currentPageId: ReturnType<typeof getSelectedGalleryItemId> = yield select(getSelectedGalleryItemId)

  const selectedRecord = currentForm.selectedRecord || 0
  const { ContentFormId } = currentForm
  const selectedTabs = currentForm.selectedTabs || []
  const [createdAnswer] = action.payload

  const answerTabs = createdAnswer.FormTabAnswers ? createdAnswer.FormTabAnswers.map((item) => item.FormTabId) : []

  const isCurrentTabsAnswer = formTabsIdsEquals(answerTabs, selectedTabs)

  if (!isCurrentTabsAnswer || createdAnswer.ContentFormId !== ContentFormId || selectedRecord > 0 || isReviewer) {
    return
  }

  const isAllLatest = displayAnswersMode === DisplayAnswersMode.AllLatest

  const isAllMyLatest = displayAnswersMode === DisplayAnswersMode.AllMyLatest && createdAnswer.UserId === selectedUserId

  const isByUser =
    displayAnswersMode === DisplayAnswersMode.ByUser &&
    createdAnswer.UserId === selectedUserId &&
    createdAnswer.PageId === currentPageId

  if (isAllLatest || isAllMyLatest || isByUser) yield put(updateFormAnswers(createdAnswer))
}

function* onAnswerStatusUpdated(action: Action<[FormControlAnswer]>): SagaIterator {
  const [updatedAnswers] = action.payload

  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const isReviewer: ReturnType<typeof getIsCurrentUserReviewer> = yield select(getIsCurrentUserReviewer)
  const selectedRecord = currentForm.selectedRecord || 0

  if (selectedRecord > 0 || currentForm.answers?.length === 0 || isReviewer) {
    return
  }

  yield put(updateAnswerStatus(updatedAnswers))
}

function* onAnswersCleared(action: Action<[{ FormAnswerId: number }]>): SagaIterator {
  const [model] = action.payload

  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const displayAnswersMode: ReturnType<typeof getDisplayAnswersMode> = yield select(getDisplayAnswersMode)
  const selectedRecord = currentForm.selectedRecord || 0

  if (selectedRecord > 0) return

  const answer = currentForm.answers?.find((item) => item.Id === model.FormAnswerId)

  if (answer) {
    yield put(clearFormAnswers(answer))

    if (displayAnswersMode === DisplayAnswersMode.AllLatest) {
      yield put(getLatestFormAnswers())
    } else if (displayAnswersMode === DisplayAnswersMode.AllMyLatest) {
      yield put(getAllMyLatestAnswers())
    }
  }
}

function* onAnswerStatusStatisticsUpdated(action: Action<[ContentFormListResponse, number]>): SagaIterator {
  const [statistics, contentFormId] = action.payload

  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  if (currentForm.ContentFormId !== contentFormId) {
    return
  }

  yield put(setAnswersStatusesCount(statistics.FormAnswerStatuses!))
}

function* formAnswers() {
  yield all([
    takeLatest(getAllMyLatestAnswers, onGetAllMyLatestAnswers),
    takeLatest(
      [
        getFormAnswers.success,
        apiGetLatestFormAnswers.success,
        getFormAnswersByRecord.success,
        getTabsAnswers.success,
        getLatestTabsAnswers.success,
      ],
      onSetFormAnswers
    ),
    takeLatest([getReadonlyContentFormSuccess, getReadonlyFormAnswerByTabSuccess], onSetReadonlyFormAnswers),
    takeLatest([getLatestFormAnswers, onReviewFeedbackSent, onContentReviewCompleted], onGetLatestFormAnswers),
    takeLatest(
      [
        setSelectedRecord,
        setDisplayAnswersMode,
        setSelectedTabs,
        selectGalleryItem,
        deleteGalleryItemSuccess,
        onDeleteGalleryItem,
        signalrEvents[signalrConstants.tabeebHubName].onPagesRestored,
      ],
      onGetFormAnswers
    ),
    takeLatest(proceedToControlAnswerPage, onProceedToControlAnswerPage),

    // signalr
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onAnswersCreated, onAnswersCreated),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onAnswersCleared, onAnswersCleared),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onAnswerStatusUpdated, onAnswerStatusUpdated),
    takeLatest(
      signalrEvents[signalrConstants.tabeebHubName].onAnswerStatusStatisticsUpdated,
      onAnswerStatusStatisticsUpdated
    ),
  ])
}

export default formAnswers
